import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import WorkspaceManager from './WorkspaceManager';
import PermissionsManager from './PermissionsManager';
import { useKanban } from './KanbanContext';
import useEscapeKey from '../utils/useEscapeKey';

function Profile() {
  const { currentUser, userData, apiLoading, apiError, logout } = useAuth();
  const [isWorkspaceManagerOpen, setIsWorkspaceManagerOpen] = useState(false);
  const [isPermissionsManagerOpen, setIsPermissionsManagerOpen] = useState(false);
  const { getUserPermissions } = useKanban();
  const userPermissions = getUserPermissions();

  // Cerrar modales con la tecla Escape
  useEscapeKey(() => {
    if (isPermissionsManagerOpen) {
      setIsPermissionsManagerOpen(false);
    } else if (isWorkspaceManagerOpen) {
      setIsWorkspaceManagerOpen(false);
    }
  }, isWorkspaceManagerOpen || isPermissionsManagerOpen);

  // Manejar el botón de retroceso del navegador para modales
  useEffect(() => {
    // Solo agregar un estado al historial cuando se abre un modal
    if (isWorkspaceManagerOpen || isPermissionsManagerOpen) {
      // Agregar una entrada al historial cuando se abre un modal
      window.history.pushState({ modal: true }, "");
      
      // Manejador para el evento popstate (botón atrás)
      const handlePopState = (event) => {
        // Cerrar modales en orden de prioridad
        if (isPermissionsManagerOpen) {
          setIsPermissionsManagerOpen(false);
        } else if (isWorkspaceManagerOpen) {
          setIsWorkspaceManagerOpen(false);
        }
        
        // Prevenir que el navegador vaya hacia atrás realmente
        if (event.state !== null) {
          window.history.pushState({ modal: true }, "");
        }
      };
      
      // Agregar el manejador de eventos
      window.addEventListener('popstate', handlePopState);
      
      // Limpiar el manejador al desmontar
      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, [isWorkspaceManagerOpen, isPermissionsManagerOpen]);

  const handleSignOut = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  if (!currentUser) {
    return null;
  }

  return (
    <div className="profile-container">
      <div className="profile-header">
        <h2>Mi Perfil</h2>
      </div>

      <div className="profile-content">
        <div className="profile-card user-basic-info">
          <div className="profile-image-container">
            {currentUser.photoURL ? (
              <img 
                src={currentUser.photoURL} 
                alt="Foto de Perfil" 
                className="profile-image" 
              />
            ) : (
              <div className="profile-image-placeholder">
                {currentUser.displayName ? currentUser.displayName.charAt(0).toUpperCase() : currentUser.email.charAt(0).toUpperCase()}
              </div>
            )}
          </div>

          <div className="profile-details">
            <h3>{currentUser.displayName || 'Usuario'}</h3>
            <p className="user-email">{currentUser.email}</p>
          </div>
        </div>

        {apiLoading ? (
          <div className="profile-card loading-card">
            <div className="loading-spinner"></div>
            <p>Cargando información adicional...</p>
          </div>
        ) : apiError ? (
          <div className="profile-card error-card">
            <div className="error-icon">!</div>
            <div className="error-details">
              <h3>Error al cargar datos</h3>
              <p>{apiError}</p>
            </div>
          </div>
        ) : userData ? (
          <div className="profile-card additional-info-card">
            <h3 className="card-title">Información laboral</h3>
            
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">RUT</span>
                <span className="info-value">{userData.body?.rut || 'No disponible'}</span>
              </div>
              
              <div className="info-item">
                <span className="info-label">Cargo</span>
                <span className="info-value">{userData.body?.title || 'No disponible'}</span>
              </div>
              
              <div className="info-item">
                <span className="info-label">Departamento</span>
                <span className="info-value">{userData.body?.department || 'No disponible'}</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="profile-card no-data-card">
            <p>No hay información adicional disponible</p>
          </div>
        )}
        
        {/* Botones para administrar tableros y permisos - solo visibles para administradores */}
        {userPermissions?.admin && (
          <div className="profile-card board-management-card">
            <h3 className="card-title">Administración</h3>
            <div className="admin-buttons-container">
              <button 
                className="manage-boards-btn"
                onClick={() => setIsWorkspaceManagerOpen(true)}
                style={{ backgroundColor: '#A3CB3F', borderColor: '#A3CB3F' }}
              >
                Administrar Tableros
              </button>
              <button 
                className="manage-permissions-btn"
                onClick={() => setIsPermissionsManagerOpen(true)}
                style={{ backgroundColor: '#A3CB3F', borderColor: '#A3CB3F' }}
              >
                Administrar Permisos
              </button>
            </div>
          </div>
        )}
        
        <div className="profile-card logout-container">
          <button className="logout-button" onClick={handleSignOut}>
            Cerrar Sesión
          </button>
        </div>
      </div>
      
      {/* Workspace Manager Modal */}
      {isWorkspaceManagerOpen && (
        <div className="workspace-manager-overlay" onClick={() => setIsWorkspaceManagerOpen(false)}>
          <div className="workspace-manager-modal" onClick={(e) => e.stopPropagation()}>
            <WorkspaceManager onClose={() => setIsWorkspaceManagerOpen(false)} />
          </div>
        </div>
      )}

      {/* Permissions Manager Modal */}
      {isPermissionsManagerOpen && (
        <div className="permissions-manager-overlay" onClick={() => setIsPermissionsManagerOpen(false)}>
          <div className="permissions-manager-modal" onClick={(e) => e.stopPropagation()}>
            <PermissionsManager onClose={() => setIsPermissionsManagerOpen(false)} />
          </div>
        </div>
      )}
    </div>
  );
}

export default Profile; 